import { useCallback } from 'react'

import { Ok } from 'ts-results'

import { useApplicationStore } from 'app/application/infrastructure'
import { useApi } from 'app/common/application'

import {
  AccountsGetRequest,
  AccountsGetResponse,
  ConnectionName,
  OAuthGetRequest,
  OAuthGetResponse,
  SupportedAccount,
  ValidateGetRequest,
  ValidateGetResponse,
} from '../types'

const getGetAccountsEndpointName = (connectionName: ConnectionName) => {
  switch (connectionName) {
    case 'criteo':
    case 'dv360':
      return 'getAdvertisers'
    case 'googleAnalytics':
      return 'getAccounts'
    default:
      return 'getAdAccounts'
  }
}

const useConnectionsApi = (connName: ConnectionName) => {
  const { create } = useApi()

  const updateConnectionData = useApplicationStore((state) => state.updateConnection)

  const getAccounts = useCallback(async () => {
    const result = await create<AccountsGetRequest, AccountsGetResponse<SupportedAccount>>(
      `/connections/${connName}/${getGetAccountsEndpointName(connName)}`,
      { content: {} },
    )
    if (result.ok) {
      return Ok(result.val.data)
    }

    return result
  }, [create, connName])

  const getOAuth = useCallback(async () => {
    const result = await create<OAuthGetRequest, OAuthGetResponse>(
      `/connections/${connName}/oauth/get`,
      { content: {} },
    )
    if (result.ok) {
      updateConnectionData(result.val.data)
      return Ok(result.val.data)
    }

    return result
  }, [create, connName, updateConnectionData])

  const validateConnection = useCallback(
    async <V>(config: V) => {
      const result = await create<ValidateGetRequest<V>, ValidateGetResponse>(
        `/connections/${connName}/validate`,
        {
          content: config,
        },
      )
      if (result.ok) {
        return Ok(result.val.data)
      }

      return result
    },
    [create, connName],
  )

  return {
    getAccounts,
    getOAuth,
    validateConnection,
  }
}

export default useConnectionsApi
